import { Filter } from 'bad-words';

// Initialize the filter
const filter = new Filter();

// Add custom banned words (expand as needed)
const CUSTOM_BANNED_WORDS = [
    'holocaust', 'isis', 'terrorist', 'nazi', 'hitler',
    // Add more specific banned words here
];

// Add custom words to the filter
filter.addWords(...CUSTOM_BANNED_WORDS);

// Check if username is available
export const checkUsernameAvailability = async (username) => {
    try {
        const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5001/api'}/auth/check-username`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ username })
        });
        const data = await response.json();
        return { available: data.available, error: data.error };
    } catch (error) {
        return { available: false, error: 'Error checking username availability' };
    }
};

// Validate username
export const validateUsername = (username) => {
    // Check length
    if (username.length < 3 || username.length > 20) {
        return { 
            valid: false, 
            error: "Username must be between 3 and 20 characters" 
        };
    }

    // Check for profanity using bad-words package
    if (filter.isProfane(username)) {
        return { 
            valid: false, 
            error: "Username contains inappropriate content",
            isBanned: true
        };
    }

    // Additional check for custom banned words
    if (CUSTOM_BANNED_WORDS.some(word => username.toLowerCase().includes(word))) {
        return { 
            valid: false, 
            error: "Username contains inappropriate content",
            isBanned: true
        };
    }

    // Check for valid characters (letters, numbers, underscores only)
    if (!/^[a-zA-Z0-9_]+$/.test(username)) {
        return { 
            valid: false, 
            error: "Username can only contain letters, numbers, and underscores" 
        };
    }

    return { valid: true, error: null };
};

// Create user document with username
// export const createUserDocument = async (user, username) => { ... }; // Removed

// Get user's display username
export const getUserDisplayName = async (userId) => { // Token is no longer needed
    try {
        // The endpoint is public, so no token or Authorization header is required.
        const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5001/api'}/auth/user/${userId}/display-name`);
        const data = await response.json();
        if (!response.ok) {
            // Return a default/anonymous name on error, but log the error for debugging.
            console.error('Failed to get display name:', data.error);
            return { displayUsername: 'Anonymous', error: data.error || 'Failed to get display name' };
        }
        return { displayUsername: data.displayUsername, error: null };
    } catch (error) {
        console.error('Error fetching display name:', error);
        return { displayUsername: 'Anonymous', error: error.message };
    }
};

// Get user verification status
// export const getUserVerificationStatus = async (userId, user) => { ... }; // Removed

// New authentication functions to call backend

export const registerWithEmailAndPassword = async (email, password, username, displayName) => {
    try {
        const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5001/api'}/auth/register`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email, password, username, displayName })
        });
        const data = await response.json();
        if (!response.ok) {
            return { success: false, error: data.error || 'Failed to register' };
        }
        // Backend returns user and the custom token, ensure key name matches backend response.
        return { success: true, user: data.user, customToken: data.customToken }; 
    } catch (error) {
        return { success: false, error: error.message || 'Error registering' };
    }
};

export const loginWithEmailAndPassword = async (email, password) => {
    try {
        const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5001/api'}/auth/login`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email, password })
        });
        const data = await response.json();
        if (!response.ok) {
            return { success: false, error: data.error || 'Failed to login' };
        }
        // EXPECT backend to return user and customToken for client to sign in with Firebase
        return { success: true, user: data.user, customToken: data.customToken };
    } catch (error) {
        return { success: false, error: error.message || 'Error logging in' };
    }
};

export const signInWithGoogleToken = async (googleIdToken) => {
    try {
        const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5001/api'}/auth/google-signin`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ token: googleIdToken }) // Send Google ID token to backend
        });
        const data = await response.json();
        if (!response.ok) {
            return { success: false, error: data.error || 'Google sign-in failed' };
        }
        // Assuming backend returns user and customToken for Google sign-in as well for consistency
        return { success: true, user: data.user, customToken: data.customToken };
    } catch (error) {
        return { success: false, error: error.message || 'Error with Google sign-in' };
    }
};

export const logoutUser = async (token) => {
    try {
        const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5001/api'}/auth/logout`, {
            method: 'POST',
            headers: { 
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}` 
            }
        });
        if (!response.ok) {
            const data = await response.json();
            return { success: false, error: data.error || 'Logout failed' };
        }
        return { success: true };
    } catch (error) {
        return { success: false, error: error.message || 'Error logging out' };
    }
};

export const getCurrentUser = async (token) => {
    try {
        const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5001/api'}/auth/me`, {
            headers: { 
                'Authorization': `Bearer ${token}` 
            }
        });
        const data = await response.json();
        if (!response.ok) {
            return { user: null, error: data.error || 'Failed to fetch user' };
        }
        return { user: data, error: null };
    } catch (error) {
        return { user: null, error: error.message || 'Error fetching current user' };
    }
};

export const requestStudentVerification = async (email, token) => {
    try {
        const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5001/api'}/auth/request-student-verification`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({ email })
        });
        const data = await response.json();
        if (!response.ok) {
            throw new Error(data.error || 'Failed to request verification');
        }
        return data;
    } catch (error) {
        throw error;
    }
}; 